import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Container, Typography, List, ListItemButton,
  ListItemText, Divider, Chip,
} from "@mui/material";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { NavLink } from "react-router-dom";
import { BRAND } from "../theme";
import { SectionAccentContext } from "./SectionAccentContext";

/* Desplaza hasta el indicador compensando navbar (y chips móviles) */
function scrollToId(id) {
  const el = document.getElementById(id);
  if (!el) return;
  const esDesktop = window.matchMedia("(min-width: 900px)").matches;
  const offset = esDesktop ? 88 : 132;
  const y = el.getBoundingClientRect().top + window.scrollY - offset;
  window.scrollTo({ top: y, behavior: "smooth" });
}

/* Decoración del hero: matriz de puntos + línea ascendente sutil */
function HeroDecoracion() {
  return (
    <Box
      aria-hidden
      component="svg"
      viewBox="0 0 900 320"
      preserveAspectRatio="xMaxYMid slice"
      sx={{
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        pointerEvents: "none",
        opacity: 0.55,
      }}
    >
      <defs>
        <pattern id="dotsSeccion" width="26" height="26" patternUnits="userSpaceOnUse">
          <circle cx="1.6" cy="1.6" r="1.6" fill="rgba(255,255,255,0.13)" />
        </pattern>
        <linearGradient id="areaSeccion" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#fff" stopOpacity="0.12" />
          <stop offset="100%" stopColor="#fff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect x="420" y="0" width="480" height="320" fill="url(#dotsSeccion)" />
      <path
        d="M420,260 C500,250 560,200 640,205 C720,210 760,150 830,128 C860,119 885,108 900,102 L900,320 L420,320 Z"
        fill="url(#areaSeccion)"
      />
      <path
        d="M420,260 C500,250 560,200 640,205 C720,210 760,150 830,128 C860,119 885,108 900,102"
        fill="none"
        stroke="rgba(255,255,255,0.4)"
        strokeWidth="2"
      />
      <circle cx="640" cy="205" r="4" fill="rgba(255,255,255,0.75)" />
      <circle cx="830" cy="128" r="4" fill={BRAND.fesYellow} opacity="0.9" />
    </Box>
  );
}

export default function SectionLayout({
  title,
  description,
  tag,
  color,
  palette,
  indicadores,
  children,
}) {
  const accent = palette?.main || color || BRAND.nodoBlue;
  const accentDark = palette?.dark || BRAND.nodoBlueDk;
  const accentSoft = palette?.soft || `${accent}14`;

  const [active, setActive] = useState(indicadores[0]?.id || "");

  const contexto = useMemo(
    () => ({ accent, dark: accentDark, soft: accentSoft }),
    [accent, accentDark, accentSoft]
  );

  /* Scrollspy: marca el indicador visible mientras se navega la página */
  useEffect(() => {
    const els = indicadores
      .map((i) => document.getElementById(i.id))
      .filter(Boolean);
    if (!els.length || typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visibles = entries.filter((e) => e.isIntersecting);
        if (!visibles.length) return;
        const superior = visibles.sort(
          (a, b) => a.boundingClientRect.top - b.boundingClientRect.top
        )[0];
        setActive(superior.target.id);
      },
      { rootMargin: "-130px 0px -55% 0px", threshold: 0 }
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [indicadores]);

  const irA = (id) => {
    setActive(id);
    scrollToId(id);
  };

  return (
    <SectionAccentContext.Provider value={contexto}>
      <Box sx={{ bgcolor: BRAND.bg, minHeight: "100vh" }}>
        {/* ===== Hero de sección ===== */}
        <Box
          sx={{
            position: "relative",
            overflow: "hidden",
            background: `linear-gradient(118deg, ${accentDark} 0%, ${accent} 62%, ${accent} 100%)`,
            color: "#fff",
            py: { xs: 5, md: 7 },
          }}
        >
          <HeroDecoracion />
          <Container maxWidth="lg" sx={{ position: "relative" }}>
            {/* Breadcrumb */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 2.5 }}>
              <Box
                component={NavLink}
                to="/"
                sx={{
                  color: "rgba(255,255,255,0.75)",
                  fontSize: "0.8rem",
                  fontWeight: 500,
                  textDecoration: "none",
                  "&:hover": { color: "#fff", textDecoration: "underline", textUnderlineOffset: "3px" },
                }}
              >
                Inicio
              </Box>
              <ChevronRightIcon sx={{ fontSize: 15, color: "rgba(255,255,255,0.55)" }} />
              <Typography sx={{ color: "#fff", fontSize: "0.8rem", fontWeight: 600 }}>
                {title}
              </Typography>
            </Box>

            {tag && (
              <Chip
                label={tag}
                size="small"
                sx={{
                  bgcolor: "rgba(255,255,255,0.16)",
                  color: "#fff",
                  mb: 2,
                  fontWeight: 600,
                  letterSpacing: 0.3,
                  backdropFilter: "blur(4px)",
                  border: "1px solid rgba(255,255,255,0.22)",
                }}
              />
            )}

            <Typography variant="h1" component="h1" sx={{ fontSize: "clamp(1.9rem, 4vw, 2.7rem)", mb: 1.5 }}>
              {title}
            </Typography>

            <Typography
              sx={{
                maxWidth: 700,
                color: "rgba(255,255,255,0.88)",
                lineHeight: 1.75,
                fontSize: { xs: "0.93rem", md: "1rem" },
              }}
            >
              {description}
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", gap: 2.5, mt: 3, flexWrap: "wrap" }}>
              <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "#fff" }}>
                {indicadores.length} indicadores
              </Typography>
              <Box aria-hidden sx={{ width: 4, height: 4, borderRadius: "50%", bgcolor: "rgba(255,255,255,0.5)" }} />
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                <FileDownloadOutlinedIcon sx={{ fontSize: 16, color: "rgba(255,255,255,0.8)" }} />
                <Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.8)" }}>
                  Descarga abierta en PNG y CSV
                </Typography>
              </Box>
            </Box>
          </Container>
        </Box>

        {/* ===== Navegación móvil: chips horizontales pegadas ===== */}
        <Box
          sx={{
            display: { xs: "flex", md: "none" },
            position: "sticky",
            top: 67,
            zIndex: 1100,
            gap: 1,
            px: 2,
            py: 1.25,
            overflowX: "auto",
            bgcolor: "rgba(244,246,251,0.92)",
            backdropFilter: "blur(10px)",
            borderBottom: `1px solid ${BRAND.border}`,
            "&::-webkit-scrollbar": { display: "none" },
            scrollbarWidth: "none",
          }}
        >
          {indicadores.map((ind) => {
            const activo = active === ind.id;
            return (
              <Chip
                key={ind.id}
                label={ind.label}
                size="small"
                clickable
                onClick={() => irA(ind.id)}
                sx={{
                  flexShrink: 0,
                  fontWeight: 600,
                  bgcolor: activo ? accent : "#fff",
                  color: activo ? "#fff" : BRAND.textSub,
                  border: `1px solid ${activo ? accent : BRAND.border}`,
                  "&:hover": { bgcolor: activo ? accent : accentSoft },
                }}
              />
            );
          })}
        </Box>

        {/* ===== Layout: sidebar + contenido ===== */}
        <Container maxWidth="lg" sx={{ py: { xs: 4, md: 6.5 } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "232px 1fr" },
              gap: { xs: 4, md: 6 },
            }}
          >
            {/* Sidebar índice (desktop) */}
            <Box
              component="aside"
              sx={{
                display: { xs: "none", md: "block" },
                position: "sticky",
                top: 88,
                maxHeight: "calc(100vh - 110px)",
                overflowY: "auto",
                alignSelf: "start",
                pr: 0.5,
              }}
            >
              <Typography
                sx={{
                  fontSize: "0.68rem",
                  fontWeight: 700,
                  letterSpacing: "0.14em",
                  color: BRAND.textFaint,
                  mb: 1.5,
                  textTransform: "uppercase",
                }}
              >
                En esta sección
              </Typography>

              <List dense disablePadding>
                {indicadores.map((ind, i) => {
                  const activo = active === ind.id;
                  return (
                    <ListItemButton
                      key={ind.id}
                      selected={activo}
                      onClick={() => irA(ind.id)}
                      sx={{
                        borderRadius: 2,
                        mb: 0.25,
                        px: 1.25,
                        py: 0.8,
                        alignItems: "flex-start",
                        gap: 1,
                        "&.Mui-selected": {
                          bgcolor: accentSoft,
                          "&:hover": { bgcolor: accentSoft },
                        },
                      }}
                    >
                      <Typography
                        component="span"
                        aria-hidden
                        sx={{
                          fontSize: "0.66rem",
                          fontWeight: 700,
                          fontVariantNumeric: "tabular-nums",
                          color: activo ? accent : BRAND.textFaint,
                          pt: "3px",
                          minWidth: 18,
                          transition: "color 0.15s ease",
                        }}
                      >
                        {String(i + 1).padStart(2, "0")}
                      </Typography>
                      <ListItemText
                        primary={ind.label}
                        primaryTypographyProps={{
                          fontSize: "0.82rem",
                          lineHeight: 1.35,
                          fontWeight: activo ? 700 : 500,
                          color: activo ? accent : BRAND.textSub,
                        }}
                      />
                    </ListItemButton>
                  );
                })}
              </List>

              <Divider sx={{ my: 2 }} />
              <Typography variant="caption" sx={{ color: BRAND.textFaint, lineHeight: 1.55, display: "block" }}>
                Todos los indicadores incluyen descarga en PNG y CSV con fuente y autoría.
              </Typography>
            </Box>

            {/* Contenido principal */}
            <Box sx={{ minWidth: 0 }}>{children}</Box>
          </Box>
        </Container>
      </Box>
    </SectionAccentContext.Provider>
  );
}
